import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { ReactNode } from 'react';

import { currencyNameSymbolMap } from '../../../../../utils/CurrencyHelper';
import { PotentialRevenueWidget } from '../../../../composableAnalytics/DerivedCharts/helper';
import { resolveTimeSpan } from '../../../../jobs/listing/components/viewJd/potentialRevenue/PotentialRevenue';
import { renderCellFor, renderDateCell } from '../../helper';
import { IPotentialRevenueDTO } from '../../jobs/types/postgreJobsDTO';

const renderRevenueCell = (
  param: GridRenderCellParams,
  showTimeSpan = true,
  timeSpan?: string,
  customValue?: string
): ReactNode => {
  const rawValue: string = customValue ?? param.value;
  if (!rawValue?.trim()) return '0 USD';

  let salaryTimeSpanLabel = '';
  if (resolveTimeSpan(param.row.salaryTimeSpan)) {
    if (timeSpan) {
      salaryTimeSpanLabel = timeSpan;
    } else if (showTimeSpan) {
      salaryTimeSpanLabel = `/${resolveTimeSpan(param.row.salaryTimeSpan)}`;
    }
  }
  const revenueItems = rawValue
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => {
      const [valueStr, currency] = item.split(' ');
      const valueNum = Number(Number(valueStr).toFixed(2));
      const formattedValue = isNaN(valueNum) ? '0' : valueNum.toLocaleString();
      const currencySymbol = currencyNameSymbolMap[currency];
      if (currencySymbol) {
        return `${currencySymbol}${formattedValue}${salaryTimeSpanLabel}`.trim();
      }
      return `${formattedValue}${currency ? ` ${currency}${salaryTimeSpanLabel}` : ''}`.trim();
    });

  return revenueItems.join(', ');
};

export const getPotentialRevenueColumns = (widgetName: PotentialRevenueWidget): GridColDef[] => {
  const renderTextCell = (param) =>
    param.value == null || param.value == '__MISSING__' ? 'Not Available' : param.value;

  const column = {
    CLIENT: {
      field: 'clientName',
      headerName: 'Client Name',
      minWidth: 120,
      flex: 1.5,
      renderCell: (param) => renderCellFor('CLIENT', param),
    },
    CLIENT_CONTACT: {
      field: 'clientRecruiterName',
      headerName: 'Client SPOC',
      minWidth: 160,
      flex: 1.5,
      renderCell: (param) => renderCellFor('CLIENT_RECRUITER', param),
    },
    GROSS_REVENUE: (hide = false) => ({
      field: 'grossRevenue',
      headerName: 'Gross Revenue',
      minWidth: 160,
      flex: 1,
      renderCell: (param) => renderRevenueCell(param, false),
      hide,
    }),
    NET_REVENUE: {
      field: 'netRevenue',
      headerName: 'Net Revenue',
      minWidth: 160,
      flex: 1,
      renderCell: (param) => renderRevenueCell(param, false),
    },
    NET_REVENUE_GBP: {
      field: 'netRevenueGBPValue',
      headerName: 'Net Revenue in GBP',
      minWidth: 160,
      flex: 1,
      renderCell: (param) => renderRevenueCell(param, false),
    },
    NET_MARGIN: {
      field: 'netMargin',
      headerName: 'Net Margin',
      minWidth: 160,
      flex: 1,
      renderCell: renderRevenueCell,
    },
    NET_MARGIN_GBP: {
      field: 'netMarginGBPValue',
      headerName: 'Net Margin in GBP',
      minWidth: 160,
      flex: 1,
      renderCell: renderRevenueCell,
    },
    NET_MARGIN_GBP_DAILY: {
      field: 'netMarginGBPDaily',
      headerName: 'Net Margin in GBP',
      minWidth: 180,
      flex: 1,
      renderCell: (param) => {
        const isJobPermanent = param.row.jobType == 'Permanent';
        const revenue = isJobPermanent ? param.row.netMarginGBPValue : param.value;
        return renderRevenueCell(param, true, '/day', revenue);
      },
    },
    ACCOUNT_MANAGER: {
      field: 'accountManagerName',
      headerName: 'Account Manager',
      minWidth: 160,
      flex: 1.5,
      renderCell: (param) => renderCellFor('ACCOUNT_MANAGER', param),
    },
    CANDIDATE: {
      field: 'candidateName',
      headerName: 'Candidate Name',
      minWidth: 160,
      flex: 1.5,
      renderCell: (param) => renderCellFor('CANDIDATE', param),
    },
    STAGE: (hide = false) => ({
      field: 'applicationStage',
      headerName: 'Stage',
      minWidth: 110,
      flex: 1,
      renderCell: renderTextCell,
      hide,
    }),
    STATE: (hide = false) => ({
      field: 'applicationState',
      headerName: 'State',
      minWidth: 130,
      flex: 1,
      renderCell: renderTextCell,
      hide,
    }),
    UPDATE_DATE: (hide = false) => ({
      field: 'modifiedOn',
      headerName: 'Updated Date',
      minWidth: 130,
      flex: 1,
      renderCell: renderDateCell,
      hide,
    }),
    JOINING_DATE: (hide = false) => ({
      field: 'joiningDate',
      headerName: 'Joining Date',
      minWidth: 130,
      flex: 1,
      renderCell: renderDateCell,
      hide,
    }),
    JOB_TITLE: (hide = false) => ({
      field: 'jobTitle',
      headerName: 'Job Title (Job Code)',
      minWidth: 300,
      flex: 1,
      renderCell: (param) =>
        renderCellFor('JOB', param, `${param.row.jobTitle} (${param.row.jobCode})`),
      hide,
    }),
  };

  switch (widgetName) {
    case PotentialRevenueWidget.TABLE_CLIENT_PERMANENT:
      return [column.CLIENT, column.NET_REVENUE, column.NET_REVENUE_GBP];
    case PotentialRevenueWidget.TABLE_CLIENT_CONTRACT:
      return [
        column.CLIENT,
        column.GROSS_REVENUE(),
        column.NET_REVENUE,
        column.NET_REVENUE_GBP,
        column.NET_MARGIN,
        column.NET_MARGIN_GBP_DAILY,
      ];
    case PotentialRevenueWidget.TABLE_CLIENT_CONTACT_PERMANENT:
      return [column.CLIENT_CONTACT, column.CLIENT, column.NET_REVENUE, column.NET_REVENUE_GBP];
    case PotentialRevenueWidget.TABLE_CLIENT_CONTACT_CONTRACT:
      return [
        column.CLIENT_CONTACT,
        column.CLIENT,
        column.GROSS_REVENUE(),
        column.NET_REVENUE,
        column.NET_REVENUE_GBP,
        column.NET_MARGIN,
        column.NET_MARGIN_GBP_DAILY,
      ];
    case PotentialRevenueWidget.TABLE_AM_CONTRACT:
      return [
        column.ACCOUNT_MANAGER,
        column.CLIENT,
        column.GROSS_REVENUE(),
        column.NET_REVENUE,
        column.NET_REVENUE_GBP,
        column.NET_MARGIN,
        column.NET_MARGIN_GBP_DAILY,
      ];
    case PotentialRevenueWidget.TABLE_AM_PERMANENT:
      return [column.ACCOUNT_MANAGER, column.CLIENT, column.NET_REVENUE, column.NET_REVENUE_GBP];
    case PotentialRevenueWidget.TABLE_CANDIDATE:
    case PotentialRevenueWidget.TABLE_CANDIDATE_BDEF:
      return [
        column.CANDIDATE,
        column.JOB_TITLE(widgetName == PotentialRevenueWidget.TABLE_CANDIDATE),
        column.CLIENT,
        column.CLIENT_CONTACT,
        column.ACCOUNT_MANAGER,
        column.STAGE(widgetName == PotentialRevenueWidget.TABLE_CANDIDATE),
        column.STATE(widgetName == PotentialRevenueWidget.TABLE_CANDIDATE),
        column.UPDATE_DATE(widgetName == PotentialRevenueWidget.TABLE_CANDIDATE),
        column.JOINING_DATE(widgetName == PotentialRevenueWidget.TABLE_CANDIDATE),
        column.GROSS_REVENUE(widgetName == PotentialRevenueWidget.TABLE_CANDIDATE_BDEF),
        column.NET_REVENUE,
        column.NET_REVENUE_GBP,
        column.NET_MARGIN,
        column.NET_MARGIN_GBP_DAILY,
      ];
    default:
      throw new Error("Invalid 'PotentialRevenueWidget' type");
  }
};

export const defaultPotentialRevenueWidgetData: IPotentialRevenueDTO = {
  totalRevenue: 0,
  currency: 'XXX',
  resultSet: [],
};
